import { useState, useEffect, useRef } from "react";
import { Leaf, Check, X, ArrowRight, Award, Loader2, Sparkles, Star, TreePine, Clock, Zap, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/App";
import apiClient from "@/utils/apiClient";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLANS = [
  {
    name: "Basic Starter",
    tier: "free",
    monthly: { price: "Free", period: "" },
    annual: { price: "Free", period: "" },
    subtitle: "Begin your daily self-care",
    color: "border-spa-sage/30",
    badge: "bg-spa-sage/10 text-spa-sage",
    packageId: null,
    annualPackageId: null,
    features: [
      { text: "AI Wellness Coach (Basic)", included: true },
      { text: "Mood Tracking (5 entries/month)", included: true },
      { text: "Community Forum Access", included: true },
      { text: "Blog Access (Summaries only)", included: true },
      { text: "ME.WELL Digest (Basic tier)", included: true },
      { text: "100 Welcome SMAHOO Coins", included: true },
      { text: "1 Free Intro Session", included: true },
    ]
  },
  {
    name: "Starter+",
    tier: "basic",
    monthly: { price: "CA$9.99", period: "/mo" },
    annual: { price: "CA$8.33", period: "/mo", billed: "CA$99.90/year", save: "Save CA$19.98 (2 months free)" },
    subtitle: "Enhanced daily wellness",
    color: "border-spa-sky/40",
    badge: "bg-spa-sky/10 text-spa-sky",
    packageId: "starter-plus",
    annualPackageId: "starter-plus-annual",
    features: [
      { text: "Everything in Basic Starter", included: true },
      { text: "Unlimited Mood Tracking", included: true },
      { text: "Full Blog & Digest Access", included: true },
      { text: "Daily ME.WELL Mind-SPA Ai.2 Agent", included: true },
      { text: "Wellness Streak Bonus Rewards", included: true },
      { text: "200 Welcome SMAHOO Coins", included: true },
      { text: "Calendar & Session Booking", included: true },
    ]
  },
  {
    name: "Premium Membership",
    tier: "premium",
    monthly: { price: "CA$49.99", period: "/mo" },
    annual: { price: "CA$41.66", period: "/mo", billed: "CA$499.90/year", save: "Save CA$99.98 (2 months free)" },
    subtitle: "Full ecosystem access",
    color: "border-spa-terracotta/40 ring-2 ring-spa-terracotta/20",
    badge: "bg-spa-terracotta/10 text-spa-terracotta",
    featured: true,
    packageId: "premium",
    annualPackageId: "premium-annual",
    features: [
      { text: "Everything in Basic Starter", included: true },
      { text: "Multi-AI Coach (GPT, Claude, Gemini)", included: true },
      { text: "Unlimited Mood Tracking", included: true },
      { text: "Premium Streaming Content", included: true },
      { text: "Premium Digest & Blog Full Access", included: true },
      { text: "Priority OnCALL+ Live Access", included: true },
      { text: "Wellness Mall Exclusive Offers", included: true },
      { text: "500 Welcome + Monthly SMAHOO Coin Bonus", included: true },
    ]
  },
  {
    name: "Elite Transformation",
    tier: "elite",
    monthly: { price: "CA$99.99", period: "/mo" },
    annual: { price: "CA$83.33", period: "/mo", billed: "CA$999.90/year", save: "Save CA$199.98 (2 months free)" },
    subtitle: "1-on-1 therapeutic sessions",
    color: "border-amber-400/30",
    badge: "bg-amber-100 text-amber-700",
    icon: Star,
    packageId: "elite",
    annualPackageId: "elite-annual",
    features: [
      { text: "Everything in Premium", included: true },
      { text: "1 Check-IN & Tune-UP monthly 1-on-1 Stress Management Laughing Drama Therapy session/month", included: true },
      { text: "Mind-SPA Retreat Program Access", included: true },
      { text: "Priority OnCALL+ support", included: true },
      { text: "3x SMAHOO Coins rewards", included: true },
    ]
  },
  {
    name: "mindSET.reSET Retreat",
    tier: "retreat",
    monthly: { price: "CA$499.99", period: "/season" },
    annual: { price: "CA$499.99", period: "/season", billed: "CA$1,499.97/year (3 seasons)" },
    subtitle: "3-Season Mind-SPA, paid annually",
    color: "border-spa-moss/30 ring-2 ring-spa-moss/10",
    badge: "bg-spa-moss/10 text-spa-moss",
    highlight: true,
    icon: TreePine,
    packageId: "retreat-annual",
    annualPackageId: "retreat-annual",
    annualOnly: true,
    features: [
      { text: "Everything in Elite Transformation", included: true },
      { text: "Full 3-Season Continuum of Care Retreat", included: true },
      { text: "Monthly Tele-Edu Therapy & Webinar Access", included: true },
      { text: "1 Same-Day Laughing Drama Therapist OnCALL+ Intervention/season", included: true },
      { text: "1 Follow-Up Session per season", included: true },
      { text: "Exclusive Retreat Community Access", included: true },
    ]
  }
];

export default function Pricing() {
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [billing, setBilling] = useState("monthly");
  const [countdown, setCountdown] = useState({ d: 0, h: 0, m: 0, s: 0 });
  const [promoDismissed, setPromoDismissed] = useState(false);
  const [authPrompt, setAuthPrompt] = useState(null);
  const { user } = useAuth();
  const userTier = user?.subscription_tier || null;

  // Promo: rolling 72-hour window from a fixed anchor (resets weekly)
  useEffect(() => {
    const getPromoEnd = () => {
      const now = Date.now();
      const anchor = new Date("2026-01-01T00:00:00Z").getTime();
      const cycle = 7 * 24 * 60 * 60 * 1000; // 7 days
      const elapsed = (now - anchor) % cycle;
      const end = now - elapsed + (3 * 24 * 60 * 60 * 1000); // 3 days into cycle
      return end > now ? end : end + cycle;
    };
    const promoEnd = getPromoEnd();
    const tick = () => {
      const diff = Math.max(0, promoEnd - Date.now());
      setCountdown({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff % 86400000) / 3600000),
        m: Math.floor((diff % 3600000) / 60000),
        s: Math.floor((diff % 60000) / 1000)
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const payment = params.get("payment");
    if (sessionId && payment === "success") {
      setPaymentStatus("checking");
      pollPaymentStatus(sessionId);
    } else if (payment === "cancelled") {
      setPaymentStatus("cancelled");
    }
  }, []);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) { setPaymentStatus("timeout"); return; }
    try {
      const res = await apiClient.get(`/payments/checkout/status/${sessionId}`);
      if (res.data.payment_status === "paid") { setPaymentStatus("success"); return; }
      if (res.data.status === "expired") { setPaymentStatus("expired"); return; }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch { setPaymentStatus("error"); }
  };

  const handleCheckout = async (plan) => {
    const packageId = billing === "annual" && plan.annualPackageId ? plan.annualPackageId : plan.packageId;
    if (!packageId) { window.location.href = "/#auth"; return; }
    setLoadingPlan(packageId);
    try {
      const res = await apiClient.post('/payments/checkout/session', {
        package_id: packageId,
        origin_url: window.location.origin
      });
      if (res.data.url) window.location.href = res.data.url;
    } catch (err) {
      setLoadingPlan(null);
      if (err.response?.status === 401 || err.response?.status === 403) {
        setAuthPrompt(plan.name);
      }
    }
  };

  return (
    <div className="min-h-screen bg-spa-bone">
      <header className="border-b border-spa-border bg-white/80 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-spa-terracotta rounded-full flex items-center justify-center">
              <Leaf className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <div className="font-serif text-spa-moss leading-tight">
              <span className="text-sm font-medium">SMAHOO ME.WELL</span><br/>
              <span className="text-sm font-medium">Mind-SPA</span>
              <span className="block text-[9px] font-sans font-normal text-spa-terracotta tracking-wide">Did you SMAHOO Today?</span>
            </div>
          </a>
          <a href="/#auth" className="spa-button-primary text-sm px-6 py-2" data-testid="pricing-signin-btn">Sign In</a>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-12" data-testid="pricing-page">
        {/* Payment Status Banners */}
        {paymentStatus === "success" && (
          <div className="bg-spa-sage/10 border border-spa-sage/30 rounded-2xl p-6 mb-8 text-center" data-testid="payment-success">
            <Check className="w-10 h-10 text-spa-sage mx-auto mb-3" />
            <h2 className="font-serif text-2xl text-spa-moss mb-2">Payment Successful!</h2>
            <p className="text-spa-text-secondary mb-4">Your membership is now active. Enjoy the full SMAHOO ME.WELL experience.</p>
            <a href="/" className="spa-button-primary inline-flex items-center gap-2">Go to Dashboard <ArrowRight className="w-4 h-4" /></a>
          </div>
        )}
        {paymentStatus === "cancelled" && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-8 text-center" data-testid="payment-cancelled">
            <p className="text-amber-800">Payment was cancelled. You can try again anytime.</p>
          </div>
        )}
        {paymentStatus === "checking" && (
          <div className="bg-spa-muted rounded-2xl p-6 mb-8 text-center" data-testid="payment-checking">
            <Loader2 className="w-8 h-8 text-spa-terracotta animate-spin mx-auto mb-3" />
            <p className="text-spa-text-secondary">Verifying your payment...</p>
          </div>
        )}

        {/* Promo Banner */}
        {!promoDismissed && (countdown.d > 0 || countdown.h > 0 || countdown.m > 0) && (
          <div className="relative mb-10 rounded-2xl overflow-hidden" data-testid="promo-banner">
            <div className="bg-gradient-to-r from-[#3a4a2a] via-[#4a5a3a] to-[#3a4a2a] px-6 py-5 text-center relative">
              <button onClick={() => setPromoDismissed(true)} className="absolute top-3 right-4 text-white/40 hover:text-white transition-colors" data-testid="promo-dismiss">
                <X className="w-4 h-4" />
              </button>
              <div className="flex items-center justify-center gap-2 mb-2">
                <Zap className="w-4 h-4 text-amber-400" />
                <p className="text-amber-300 text-xs font-semibold uppercase tracking-widest">Launch Special</p>
                <Zap className="w-4 h-4 text-amber-400" />
              </div>
              <h3 className="font-serif text-2xl sm:text-3xl text-white mb-1">
                30% Off Your First 3 Months
              </h3>
              <p className="text-white/60 text-sm mb-4">Premium or Elite membership — use code <span className="font-mono bg-white/10 px-2 py-0.5 rounded text-amber-300 font-semibold">SMAHOO30</span> at checkout</p>
              <div className="flex items-center justify-center gap-3 sm:gap-5" data-testid="countdown-timer">
                {[
                  { val: countdown.d, label: "Days" },
                  { val: countdown.h, label: "Hours" },
                  { val: countdown.m, label: "Mins" },
                  { val: countdown.s, label: "Secs" }
                ].map((unit) => (
                  <div key={unit.label} className="flex flex-col items-center">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 bg-white/10 backdrop-blur rounded-xl flex items-center justify-center border border-white/10">
                      <span className="font-mono text-2xl sm:text-3xl font-bold text-white">{String(unit.val).padStart(2, '0')}</span>
                    </div>
                    <span className="text-white/40 text-[10px] uppercase tracking-wider mt-1.5">{unit.label}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4">
                <a href="#plans" className="inline-flex items-center gap-2 px-6 py-2.5 bg-amber-500 hover:bg-amber-400 text-[#3a4a2a] rounded-full text-sm font-semibold transition-colors">
                  <Clock className="w-4 h-4" /> Claim Offer Before Time Runs Out
                </a>
              </div>
            </div>
          </div>
        )}

        <div className="text-center mb-8" id="plans">
          <p className="overline-text mb-2">Membership Plans</p>
          <h1 className="font-serif text-3xl sm:text-4xl font-medium text-spa-moss mb-3">Choose Your Wellness Path</h1>
          <p className="text-spa-text-secondary max-w-xl mx-auto">Start free, go premium, or transform with elite 1-on-1 therapy and our annual Mind-SPA Retreat</p>
        </div>

        {/* Billing Toggle */}
        <div className="flex items-center justify-center gap-3 mb-10" data-testid="billing-toggle">
          <span className={`text-sm font-medium transition-colors ${billing === "monthly" ? "text-spa-moss" : "text-spa-text-secondary"}`}>Monthly</span>
          <button
            onClick={() => setBilling(billing === "monthly" ? "annual" : "monthly")}
            className="relative w-14 h-7 rounded-full transition-colors focus:outline-none"
            style={{ backgroundColor: billing === "annual" ? "#5a6b4a" : "#d4cdc4" }}
            data-testid="billing-toggle-btn"
          >
            <div className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow-md transition-transform ${billing === "annual" ? "translate-x-7" : "translate-x-0.5"}`} />
          </button>
          <span className={`text-sm font-medium transition-colors ${billing === "annual" ? "text-spa-moss" : "text-spa-text-secondary"}`}>Annual</span>
          {billing === "annual" && (
            <span className="px-2.5 py-1 bg-spa-sage/10 text-spa-sage text-xs font-medium rounded-full animate-fade-in" data-testid="save-badge">
              Save 2 months
            </span>
          )}
        </div>

        {/* Auth Prompt */}
        {authPrompt && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="auth-prompt-overlay">
            <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full mx-4 p-8 relative animate-fade-in">
              <button onClick={() => setAuthPrompt(null)} className="absolute top-4 right-4 text-spa-text-secondary hover:text-spa-moss" data-testid="auth-prompt-close">
                <X className="w-5 h-5" />
              </button>
              <div className="text-center mb-6">
                <div className="w-14 h-14 bg-spa-terracotta/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-7 h-7 text-spa-terracotta" />
                </div>
                <h3 className="font-serif text-2xl text-spa-moss mb-2">Create Your Account</h3>
                <p className="text-spa-text-secondary text-sm">
                  To subscribe to <span className="font-medium text-spa-moss">{authPrompt}</span>, you'll need an account first. It's free and takes 30 seconds!
                </p>
              </div>
              <div className="space-y-2.5 mb-6">
                <div className="flex items-center gap-2.5 text-sm text-spa-moss">
                  <Check className="w-4 h-4 text-spa-sage flex-shrink-0" /> Earn 100 SMAHOO Coins welcome bonus
                </div>
                <div className="flex items-center gap-2.5 text-sm text-spa-moss">
                  <Check className="w-4 h-4 text-spa-sage flex-shrink-0" /> Track your subscription & billing
                </div>
                <div className="flex items-center gap-2.5 text-sm text-spa-moss">
                  <Check className="w-4 h-4 text-spa-sage flex-shrink-0" /> Access the AI Coach, Mood Tracker & more
                </div>
              </div>
              <a href="/#auth" className="spa-button-primary w-full text-center block py-3 mb-3" data-testid="auth-prompt-register-btn">
                Register Free & Subscribe
              </a>
              <p className="text-center text-sm text-spa-text-secondary">
                Already a member? <a href="/#auth" className="text-spa-terracotta font-medium hover:underline" data-testid="auth-prompt-signin-link">Sign in</a>
              </p>
            </div>
          </div>
        )}

        {/* Plans Grid */}
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto mb-8">
          {PLANS.slice(0, 3).map(plan => (
            <PlanCard key={plan.name} plan={plan} billing={billing} loadingPlan={loadingPlan} handleCheckout={handleCheckout} userTier={userTier} />
          ))}
        </div>
        <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto mb-16">
          {PLANS.slice(3).map(plan => (
            <PlanCard key={plan.name} plan={plan} billing={billing} loadingPlan={loadingPlan} handleCheckout={handleCheckout} userTier={userTier} />
          ))}
        </div>

        {/* SMAHOO Coins Section */}
        <div id="coins" className="max-w-5xl mx-auto" data-testid="coins-section">
          <div className="spa-card bg-gradient-to-br from-white to-amber-50/50 border-2 border-amber-200/30">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-amber-600" />
              </div>
              <h2 className="font-serif text-2xl font-medium text-spa-moss mb-2">SMAHOO Coins</h2>
              <p className="text-spa-text-secondary max-w-lg mx-auto">Earn and spend SMAHOO Coins throughout the ecosystem</p>
            </div>
            <div className="grid sm:grid-cols-3 gap-4 mb-6">
              <div className="bg-white rounded-2xl p-4 text-center border border-amber-100">
                <p className="font-serif text-2xl text-amber-600 mb-1">100</p>
                <p className="text-sm text-spa-text-secondary">Welcome Bonus (Free)</p>
              </div>
              <div className="bg-white rounded-2xl p-4 text-center border border-amber-100">
                <p className="font-serif text-2xl text-amber-600 mb-1">500</p>
                <p className="text-sm text-spa-text-secondary">Premium Welcome Bonus</p>
              </div>
              <div className="bg-white rounded-2xl p-4 text-center border border-amber-100">
                <p className="font-serif text-2xl text-amber-600 mb-1">+50/mo</p>
                <p className="text-sm text-spa-text-secondary">Premium Monthly Bonus</p>
              </div>
            </div>
            <div className="text-center">
              <p className="text-spa-text-secondary text-sm mb-4">Earn coins through daily logins, mood tracking, community engagement, referrals, and completing wellness modules. Spend them on premium sessions, gift baskets, and exclusive content.</p>
              <a href="/#auth" className="spa-button-primary inline-flex items-center gap-2">
                Start Earning <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PlanCard({ plan, billing, loadingPlan, handleCheckout, userTier }) {
  const Icon = plan.icon || Sparkles;
  const priceData = plan.annualOnly ? plan.annual : (billing === "annual" ? plan.annual : plan.monthly);
  const isAnnual = billing === "annual" && !plan.annualOnly;
  const activePackageId = billing === "annual" && plan.annualPackageId ? plan.annualPackageId : plan.packageId;
  const isCurrentPlan = userTier && (
    (plan.tier === "free" && (userTier === "free" || userTier === "basic")) ||
    (plan.tier !== "free" && userTier === plan.tier)
  );

  return (
    <div className={`spa-card border-2 relative ${plan.color}`} data-testid={`plan-${plan.name.toLowerCase().replace(/\s/g, '-')}`}>
      {plan.featured && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-spa-terracotta text-white text-xs font-medium rounded-full">Most Popular</div>
      )}
      {plan.highlight && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-spa-moss text-white text-xs font-medium rounded-full">Annual Retreat</div>
      )}
      <div className="text-center mb-5 pt-2">
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium mb-3 ${plan.badge}`}>
          {(plan.icon) && <Icon className="w-3 h-3" />}
          {plan.name}
        </span>
        <div className="flex items-baseline justify-center gap-1">
          <p className="font-serif text-4xl text-spa-moss">{priceData.price}</p>
          {priceData.period && <span className="text-spa-text-secondary text-sm">{priceData.period}</span>}
        </div>
        <p className="text-spa-text-secondary text-sm mt-1">{plan.subtitle}</p>
        {isAnnual && priceData.billed && (
          <div className="mt-2 space-y-0.5">
            <p className="text-xs text-spa-moss/60">{priceData.billed}</p>
            <p className="text-xs font-medium text-spa-sage" data-testid={`save-text-${plan.name.toLowerCase().replace(/\s/g, '-')}`}>{priceData.save}</p>
          </div>
        )}
        {plan.annualOnly && priceData.billed && (
          <p className="text-xs text-spa-moss/60 mt-1">{priceData.billed}</p>
        )}
      </div>
      <div className="space-y-2.5 mb-6">
        {plan.features.map(f => (
          <div key={f.text} className="flex items-start gap-2.5">
            {f.included ? (
              <Check className="w-4 h-4 text-spa-sage mt-0.5 flex-shrink-0" />
            ) : (
              <X className="w-4 h-4 text-spa-border mt-0.5 flex-shrink-0" />
            )}
            <span className={`text-sm ${f.included ? "text-spa-moss" : "text-spa-text-secondary/50 line-through"}`}>
              {f.text}
            </span>
          </div>
        ))}
      </div>
      {isCurrentPlan ? (
        <div
          className="w-full flex items-center justify-center gap-2 py-3 rounded-full text-sm font-medium bg-spa-sage/10 text-spa-sage border border-spa-sage/30 cursor-default"
          data-testid={`plan-current-${plan.name.toLowerCase().replace(/\s/g, '-')}`}
        >
          <CheckCircle2 className="w-4 h-4" /> Current Plan
        </div>
      ) : (
      <button
        onClick={() => handleCheckout(plan)}
        disabled={loadingPlan !== null && loadingPlan === activePackageId}
        className={`w-full flex items-center justify-center gap-2 py-3 rounded-full text-sm font-medium transition-all ${
          plan.featured ? "spa-button-primary" 
          : plan.highlight ? "bg-spa-moss text-white hover:bg-spa-moss/90"
          : plan.icon ? "bg-amber-500 text-white hover:bg-amber-600"
          : "border border-spa-border text-spa-moss hover:bg-spa-muted"
        }`}
        data-testid={`plan-cta-${plan.name.toLowerCase().replace(/\s/g, '-')}`}
      >
        {loadingPlan !== null && loadingPlan === activePackageId ? (
          <><Loader2 className="w-4 h-4 animate-spin" /> Processing...</>
        ) : (
          <>
            {!plan.packageId ? "Join Basic" : plan.highlight ? "Begin Retreat Journey" : plan.icon ? "Go Elite" : plan.tier === "basic" ? "Get Starter+" : "Get Premium"} <ArrowRight className="w-4 h-4" />
          </>
        )}
      </button>
      )}
    </div>
  );
}
